import { Static, Type } from '@sinclair/typebox';
import { public_env } from './constants/public_env';
import { WebsiteConfigSchema } from './website_types/WebsiteTypes';
import { WebsiteDecorationStyleSchema, WebsiteRadiusStyleSchema } from './website_types/Styles';
import { WebsiteDiscoveryRequestSchema } from './website_discovery';

/**
 * URL d'inscription du funnel preview→compte (équivalent du CTA « Publier mon site »).
 * Source UNIQUE partagée par : la bannière de preview (custom-websites), la modale
 * d'inscription preview (shared-components) et le lien direct injecté dans le contexte
 * Pulse (backend). On pointe DIRECTEMENT sur le register de l'app pro avec le préfixe de
 * locale par défaut (`/fr-FR`, localePrefix 'always') pour éviter un hop de redirection.
 *
 * Le token preview (`?pt=`) permet au backend de retrouver le lead (pivot CRM + handoff
 * Pulse + hydratation du site + dérivation nom/ville). Sans token, on renvoie le register
 * nu (preview partagée hors funnel). `previewUrl` (optionnel) est joint pour que le tunnel
 * puisse embarquer la preview.
 */
export type PreviewRegisterUtm = {
  source: string;
  medium?: string;
  campaign?: string;
};

export function buildPreviewRegisterUrl(token?: string, previewUrl?: string, utm?: PreviewRegisterUtm): string {
  const base = `${public_env.APP_BASE_URL}/fr-FR/register`;
  if (!token) return base;
  const params = new URLSearchParams();
  params.set('pt', token);
  if (previewUrl) params.set('preview_url', previewUrl);
  // Canal d'acquisition « en clair » (lu par `buildSignupAttribution` sur la page
  // register, puis snapshoté dans `professional.marketing_attribution`) : c'est ce qui
  // permet de savoir qu'un inscrit vient d'un lien partagé par Pulse sur WhatsApp plutôt
  // que du bouton « Publier mon site » ou d'une arrivée directe.
  if (utm) {
    params.set('utm_source', utm.source);
    if (utm.medium) params.set('utm_medium', utm.medium);
    if (utm.campaign) params.set('utm_campaign', utm.campaign);
  }
  return `${base}?${params.toString()}`;
}

/**
 * Étapes de génération de la preview (waouh v2 §3.3) — posées par le processor aux jalons
 * RÉELS du job et lues par le widget Pulse (bulle de progression) + la bannière preview.
 * Honnêteté : un from-scratch ne passe QUE par `assembly` (jamais « je récupère tes avis »).
 *  - lookup   : résolution fiche Google / sources + scrape + extraction
 *  - photos   : ré-hébergement des photos
 *  - reviews  : qualification des avis / merge du réel
 *  - assembly : assemblage du site (synthèse from-scratch incluse)
 *  - enriching: Phase B (enrichissement scrape) en cours, preview déjà livrée
 *  - done     : terminé
 */
export const PREVIEW_GENERATION_STAGES = ['lookup', 'photos', 'reviews', 'assembly', 'enriching', 'done'] as const;
export type PreviewGenerationStage = (typeof PREVIEW_GENERATION_STAGES)[number];
export const PreviewGenerationStageSchema = Type.Union(
  PREVIEW_GENERATION_STAGES.map((stage): ReturnType<typeof Type.Literal> => Type.Literal(stage)),
);

// Proposition de style pour le switcher de la preview (Lot 4) : clé de palette + libellé +
// puces de couleurs pour la pastille. `chosen` marque le style actif (défaut sectoriel ou
// choix explicite du lead).
export const LeadPreviewStyleOptionSchema = Type.Object({
  key: Type.String({ maxLength: 60 }),
  label: Type.String({ maxLength: 60 }),
  colors: Type.Object({
    background: Type.String(),
    accent: Type.String(),
    text: Type.String(),
    cta: Type.String(),
  }),
  // Décor de fond + arrondis de la palette : renvoyés pour que le switcher applique
  // l'ambiance COMPLÈTE au clic (optimistic), sans attendre le prochain chargement.
  // La typo, elle, reste appliquée au rechargement (les fonts sont chargées serveur).
  decoration: Type.Optional(WebsiteDecorationStyleSchema),
  border_style: Type.Optional(WebsiteRadiusStyleSchema),
  chosen: Type.Optional(Type.Boolean()),
});
export type LeadPreviewStyleOptionObject = Static<typeof LeadPreviewStyleOptionSchema>;

// Body du POST /public/lead_preview/:token/style — choix explicite du lead (gagne sur la
// brand color scrapée, décision §4.1) ; préservé par la Phase B (ré-assemblage).
export const LeadPreviewStyleRequestSchema = Type.Object({
  style_key: Type.String({ minLength: 1, maxLength: 60 }),
});
export type LeadPreviewStyleRequestObject = Static<typeof LeadPreviewStyleRequestSchema>;

// Réponse de la route publique de rendu de preview (cf. docs/website-preview-pulse-plan.md).
// `prefetched_data` est la map indexée par instanceId attendue par WebsiteRenderer.
export const LeadPreviewResponseSchema = Type.Object({
  config: WebsiteConfigSchema,
  prefetched_data: Type.Record(Type.String(), Type.Unknown()),
  source_provider: Type.Optional(Type.String()),
  // Phase B (enrichissement scrape) en cours → la bannière preview affiche « j'enrichis
  // encore ton site… » et poll léger jusqu'au passage à false (bouton Actualiser).
  enriching: Type.Optional(Type.Boolean()),
  // Switcher de styles (Lot 4) : palette sectorielle par défaut + 2 alternatives.
  available_styles: Type.Optional(Type.Array(LeadPreviewStyleOptionSchema)),
  // Clé du style explicitement choisi par le lead (événement d'engagement, cité par Pulse).
  style_chosen: Type.Optional(Type.String()),
});
export type LeadPreviewResponseObject = Static<typeof LeadPreviewResponseSchema>;

/**
 * Seuils du gate qualité Phase B (§3.2-bis) : le réel n'écrase le mock que s'il passe ces
 * seuils déterministes — remplacer un mock générique mais beau par du réel moche est pire.
 * Partagés (backend qualifyExtracted + affichage builder/admin futur).
 */
export const PREVIEW_QUALITY_THRESHOLDS = {
  /** Photo hero/établissement : largeur mini + ratio paysage mini. */
  hero_photo_min_width: 1200,
  hero_photo_min_landscape_ratio: 1.2,
  /** Avis Google sur la home : note mini, volume mini, textes exploitables. */
  reviews_min_rating: 4.0,
  reviews_min_count: 5,
  reviews_min_texts: 3,
  reviews_min_text_chars: 40,
  /** Galerie : nb photos qualifiées mini + largeur mini. */
  gallery_min_photos: 4,
  gallery_photo_min_width: 800,
  /** Logo : côté mini (un favicon 32px n'est pas un logo). */
  logo_min_side: 200,
  /**
   * Couleur de marque (retour 02/11/2026, « pas d'infos d'un site rincé ») : reprise seulement si
   * le site a une identité visuelle vérifiée — un logo qualifié OU au moins ce nombre de photos
   * qualifiées. Sinon la palette sectorielle reste (une teinte seule n'embellit rien).
   */
  brand_color_min_photos: 2,
  /** Description de prestation : bornes de longueur (texte nettoyé). */
  prestation_description_min_chars: 60,
  prestation_description_max_chars: 600,
  /** Bio : bornes de longueur. */
  bio_min_chars: 200,
  bio_max_chars: 1500,
  /** FAQ / chiffres-clés : nb d'entrées cohérentes mini. */
  faq_min_entries: 3,
  key_figures_min_entries: 3,
  /** Instagram (Lot 7) : nb de permalinks valides mini. */
  instagram_min_permalinks: 3,
} as const;

// Brief de génération from-scratch (lead sans patrimoine numérique).
// Spec cible §5.5 : "On part d'une trame [secteur] [ville]". On skip
// discover/scrape/extract et on synthétise un site via LLM depuis metier+ville.
// Pas de photos réelles (placeholders). Le LLM génère prestations types, bio,
// horaires types — tout le contenu est explicitement présenté comme "à
// personnaliser" dans le rendu.
export const LeadPreviewFromScratchBriefSchema = Type.Object({
  metier: Type.String({ minLength: 2, maxLength: 120 }),
  /** `QUIZ_UNKNOWN_VILLE` quand le quiz ne l'a pas demandée (elle arrive à l'inscription). */
  ville: Type.String({ minLength: 2, maxLength: 120 }),
  /** Métier du référentiel (`activity.slug`) : résolution directe du pack de contenu par défaut. */
  activity_slug: Type.Optional(Type.String({ maxLength: 120 })),
  // ─── Onboarding v4 : brief du quiz (écrans 2 à 6). Tout optionnel, rétro-compatible. ───
  /** Où le pro exerce (pré-remplit l'étape 2 du tunnel ; la démo montre « à domicile »). */
  lieux: Type.Optional(
    Type.Array(Type.Union([Type.Literal('sur_place'), Type.Literal('deplacement'), Type.Literal('visio')]), {
      maxItems: 3,
    }),
  ),
  /** Ton voulu pour les textes (accroche, à-propos). */
  ton: Type.Optional(Type.Union([Type.Literal('chaleureux'), Type.Literal('chic'), Type.Literal('direct')])),
  /** « Ce qui vous distingue », avec les mots du pro (≤ 200). */
  phrase_distinctive: Type.Optional(Type.String({ maxLength: 200 })),
  /** Ce qui existe déjà (écran 5) : la démo ne redemande jamais ce qui est déclaré. */
  visibilite: Type.Optional(
    Type.Object({
      instagram: Type.Optional(Type.Boolean()),
      google: Type.Optional(Type.Boolean()),
      site: Type.Optional(Type.Boolean()),
      bouche_a_oreille: Type.Optional(Type.Boolean()),
    }),
  ),
  situation: Type.Optional(
    Type.Union([Type.Literal('deja_lancee'), Type.Literal('je_me_lance'), Type.Literal('projet')]),
  ),
  /** Besoins cochés, dans l'ordre (le premier pilote la promesse mise en avant). */
  besoins: Type.Optional(Type.Array(Type.String({ maxLength: 40 }), { maxItems: 5 })),
  /** Liens DONNÉS par le pro — seuls candidats à une extraction, jamais une découverte web. */
  outil_url: Type.Optional(Type.String({ maxLength: 2048 })),
  site_url: Type.Optional(Type.String({ maxLength: 2048 })),
  instagram_url: Type.Optional(Type.String({ maxLength: 2048 })),
  // Contenu fourni PAR LE LEAD (chemin collecte conversationnelle quand le
  // consentement au scraping est refusé, ou fallback robustesse). Tout optionnel
  // → rétro-compatible avec le from-scratch pur metier+ville. Quand présent, le
  // service synthétique structure ce contenu VERBATIM au lieu d'inventer.
  business_name: Type.Optional(Type.String({ maxLength: 200 })),
  description: Type.Optional(Type.String({ maxLength: 2000 })),
  prestations: Type.Optional(
    Type.Array(
      Type.Object({
        name: Type.String({ maxLength: 120 }),
        description: Type.Optional(Type.String({ maxLength: 500 })),
        price_eur: Type.Optional(Type.Number()),
        duration_min: Type.Optional(Type.Number()),
      }),
      { maxItems: 20 },
    ),
  ),
});
export type LeadPreviewFromScratchBriefObject = Static<typeof LeadPreviewFromScratchBriefSchema>;

// Requête de génération de preview : enqueue un job
// (découverte → scrape multi-sources → extraction → consolidation → assemblage).
// Au moins un de `source_urls`, `discovery` ou `from_scratch` doit être fourni.
export const LeadPreviewGenerateRequestSchema = Type.Object({
  lead_id: Type.Integer(),
  // URLs déjà connues à scraper (site officiel, page Planity collée par le pro).
  source_urls: Type.Optional(Type.Array(Type.String({ format: 'uri' }))),
  // Brief de découverte : le backend retrouve lui-même les URLs manquantes.
  discovery: Type.Optional(WebsiteDiscoveryRequestSchema),
  // Brief from-scratch : aucun patrimoine, synthèse via LLM depuis metier+ville.
  from_scratch: Type.Optional(LeadPreviewFromScratchBriefSchema),
  max_pages: Type.Optional(Type.Integer({ minimum: 1, maximum: 30 })),
});
export type LeadPreviewGenerateRequestObject = Static<typeof LeadPreviewGenerateRequestSchema>;

export const LeadPreviewGenerateResponseSchema = Type.Object({
  job_id: Type.String(),
});
export type LeadPreviewGenerateResponseObject = Static<typeof LeadPreviewGenerateResponseSchema>;

// Pré-remplissage du tunnel d'inscription preview→compte. La page register lit le
// `?pt=<token>` et appelle cet endpoint pour récupérer ce que Pulse connaît déjà du lead
// (nom commercial + ville, dérivés de `qualification.nom_pro`/`ville`). Permet de
// pré-remplir les champs ET de décider quels champs masquer (dérivables) vs afficher
// (manquants) SANS dépendre d'un aller-retour d'erreur au submit. Les deux champs sont
// optionnels : un lead qui a fait un close direct sans rien dire renvoie `{}` → la page
// affiche le formulaire classique. Pas de PII de contact (email/téléphone) exposée :
// le token preview n'est pas un secret, on ne renvoie que ce qui est déjà public/affiché.
export const LeadSignupPrefillResponseSchema = Type.Object({
  business_name: Type.Optional(Type.String()),
  city: Type.Optional(Type.String()),
  /** Lead né du quiz d'avant-inscription (`source = 'quiz'`) : `/register` passe en mode quiz. */
  from_quiz: Type.Optional(Type.Boolean()),
  // `true` si un site preview a RÉELLEMENT été généré pour ce lead (scrape ou from-scratch).
  // Pilote le wording du tunnel : « votre site est prêt » (preview existante) vs « créez votre
  // compte » (close direct / lancement sans site préparé — « récupérer votre site » serait faux).
  has_preview: Type.Optional(Type.Boolean()),
});
export type LeadSignupPrefillResponseObject = Static<typeof LeadSignupPrefillResponseSchema>;

// Page admin de debug : crée un lead minimal puis enqueue le job. Pas de `lead_id`
// en entrée (il est généré côté backend). Au moins un de `source_urls` / `discovery`.
export const LeadPreviewDebugCreateRequestSchema = Type.Object({
  source_urls: Type.Optional(Type.Array(Type.String({ format: 'uri' }))),
  discovery: Type.Optional(WebsiteDiscoveryRequestSchema),
  max_pages: Type.Optional(Type.Integer({ minimum: 1, maximum: 30 })),
});
export type LeadPreviewDebugCreateRequestObject = Static<typeof LeadPreviewDebugCreateRequestSchema>;

export const LeadPreviewDebugCreateResponseSchema = Type.Object({
  lead_id: Type.Integer(),
  job_id: Type.String(),
});
export type LeadPreviewDebugCreateResponseObject = Static<typeof LeadPreviewDebugCreateResponseSchema>;

export enum LeadPreviewDebugStatus {
  PENDING = 'pending',
  READY = 'ready',
  FAILED = 'failed',
}

export const LeadPreviewDebugStatusResponseSchema = Type.Object({
  status: Type.Enum(LeadPreviewDebugStatus),
  preview_token: Type.Optional(Type.String()),
  error: Type.Optional(Type.String()),
});
export type LeadPreviewDebugStatusResponseObject = Static<typeof LeadPreviewDebugStatusResponseSchema>;
